import React, { useState, useEffect } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { LeadSource } from "../../../../../../../../models/leadsource";
import { Status } from "../../../../../../../../util/enums";
import Input from "../../../../../../../../components/Form/Input";
import Modal from "../../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  leadSource?: LeadSource;
  open: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  updateOrCreateLeadSource: (leadSource: LeadSource) => void;
}

const LeadSourceModal = ({
  leadSource,
  open,
  hasError,
  isLoading = false,
  closeModal,
  updateOrCreateLeadSource,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [name, setName] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (type) =>
        type ? type !== "" : false
      ),
  });

  const clearFields = () => {
    setName("");
    setCode("");
    setStatus("");
  };

  const isValid = (leadSource: LeadSource) => {
    try {
      schema.validateSync(
        {
          name: leadSource.name,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      name,
      value: name,
      code,
      status,
    } as LeadSource;

    if (isValid(object)) updateOrCreateLeadSource(object);
  };

  useEffect(() => {
    if (leadSource) {
      setName(leadSource?.name);
      setCode(leadSource?.code ? leadSource?.code : "");
      setStatus(leadSource.status);
    }
  }, [leadSource]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        name,
      } as LeadSource;

      isValid(object);
    }
  }, [name, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="leadSourceModal"
      open={open}
      handleClose={closeModal}
      title={leadSource ? translate("editLeadSource") : translate("createNewLeadSource")}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        loading: isLoading,
        saveDisabled: isLoading,
        textSave: leadSource ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("leadSourceStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="code"
              label={translate("code")}
              placeholder={translate("code")}
              value={code}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCode(event.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="name"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              value={name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default LeadSourceModal;
